// components/Sidebar.jsx
import React, { useRef, useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import Logo from "../assets/ProfileSetup/logo.svg";
import AccountSetupActive from "../assets/ProfileSetup/AccountSetupActive.svg";
import AccountSetupCompleted from "../assets/ProfileSetup/AccountSetupCompleted.svg";
import AccountSetupDisabled from "../assets/ProfileSetup/AccountSetupDisabled.svg";
import EducationActive from "../assets/ProfileSetup/EducationActive.svg";
import EducationCompleted from "../assets/ProfileSetup/EducationCompleted.svg";
import EducationDisabled from "../assets/ProfileSetup/EducationDisabled.svg";
import ProfessionalActive from "../assets/ProfileSetup/ProfessionalActive.svg";
import ProfessionalCompleted from "../assets/ProfileSetup/ProfessionalCompleted.svg";
import ProfessionalDisabled from "../assets/ProfileSetup/ProfessionalDisabled.svg";
import SkillsAndExpertiseActive from "../assets/ProfileSetup/SkillsAndExpertiseActive.svg";
import SkillsAndExpertiseCompleted from "../assets/ProfileSetup/SkillsAndExpertiseCompleted.svg";
import SkillsAndExpertiseDisabled from "../assets/ProfileSetup/SkillsAndExpertiseDisabled.svg";
import "../css/Sidebar.css"; // Import the CSS file

const LogoutIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-6 h-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
    />
  </svg>
);

// Clear Data Confirmation Modal Component
const ClearDataModal = ({ isOpen, onClose, onConfirm, isClearing }) => {
  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="text-red-500 mr-3">
            <AlertTriangleIcon />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            Clear All Data
          </h3>
        </div>

        <p className="text-gray-600 mb-4">
          Are you sure you want to clear all profile data? This action cannot be
          undone and will remove:
        </p>

        <ul className="list-disc list-inside text-sm text-gray-600 mb-6 space-y-1 bg-gray-50 p-3 rounded">
          <li>Personal information and contact details</li>
          <li>Education history</li>
          <li>Work experience</li>
          <li>Skills and certifications</li>
          <li>Any AI-populated data</li>
        </ul>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isClearing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isClearing}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isClearing ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Clearing...
              </>
            ) : (
              "Clear All Data"
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render directly to document.body
  return createPortal(modalContent, document.body);
};

const Sidebar = ({
  currentStepIndex = 0,
  steps = [],
  onStepClick,
  stepDataStatus = [],
  onLogout,
  onClearData, // New prop for clearing data
}) => {
  const [showClearModal, setShowClearModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 1. Filter out unwanted steps and store their original index
  const filteredSteps = useMemo(() => {
    const excludedSections = ["preferences", "documents", "skillTest"];
    return steps
      .map((step, index) => ({ ...step, originalIndex: index }))
      .filter((step) => !excludedSections.includes(step.section || step.id));
  }, [steps]);

  // 2. Find the index in the *filtered* array that corresponds to the original currentStepIndex
  const currentFilteredIndex = useMemo(
    () =>
      filteredSteps.findIndex(
        (step) => step.originalIndex === currentStepIndex
      ),
    [filteredSteps, currentStepIndex]
  );

  const containerRef = useRef(null);
  const stepRefs = useRef([]);
  const [lineStyles, setLineStyles] = useState({
    height: 0,
    width: 0,
    left: 0,
    top: 0,
    progressHeight: 0,
    progressWidth: 0,
  });

  // Handle clear data confirmation
  const handleClearClick = () => {
    setShowClearModal(true);
  };

  const handleClearConfirm = async () => {
    if (onClearData) {
      setIsClearing(true);
      try {
        await onClearData();
        setShowClearModal(false);
      } catch (error) {
        console.error("Error clearing data:", error);
        // Keep modal open on error so user can try again
      } finally {
        setIsClearing(false);
      }
    }
  };

  const handleClearCancel = () => {
    setShowClearModal(false);
  };

  const getIconForStep = (step, index) => {
    // 'step' is an object from filteredSteps, 'index' is the filtered index
    const originalIndex = step.originalIndex;
    const hasData = stepDataStatus[originalIndex] === true;
    const section = step.section || step.id;
    let activeIcon, completedIcon, disabledIcon;

    switch (section) {
      case "account":
        activeIcon = AccountSetupActive;
        completedIcon = AccountSetupCompleted;
        disabledIcon = AccountSetupDisabled;
        break;
      case "educations":
      case "education":
        activeIcon = EducationActive;
        completedIcon = EducationCompleted;
        disabledIcon = EducationDisabled;
        break;
      case "experiences":
      case "experience":
        activeIcon = ProfessionalActive;
        completedIcon = ProfessionalCompleted;
        disabledIcon = ProfessionalDisabled;
        break;
      case "skills":
        activeIcon = SkillsAndExpertiseActive;
        completedIcon = SkillsAndExpertiseCompleted;
        disabledIcon = SkillsAndExpertiseDisabled;
        break;
      default:
        console.warn(
          `[Sidebar] Unmapped section: ${section} for icon selection. Falling back to Account icons.`
        );
        activeIcon = AccountSetupActive;
        completedIcon = AccountSetupCompleted;
        disabledIcon = AccountSetupDisabled;
    }

    // Use the filtered index for active state
    if (index === currentFilteredIndex) return activeIcon;
    // Use the original index for completed state
    if (originalIndex < currentStepIndex || hasData) return completedIcon;
    return disabledIcon;
  };

  useEffect(() => {
    const calculateLinePositions = () => {
      // Use filteredSteps.length for calculations
      if (!containerRef.current || stepRefs.current.length < 2) {
        setLineStyles({
          height: 0,
          width: 0,
          left: 0,
          top: 0,
          progressHeight: 0,
          progressWidth: 0,
        });
        return;
      }
      const firstIconElement = stepRefs.current[0];
      const lastIconElement = stepRefs.current[stepRefs.current.length - 1];
      if (!firstIconElement || !lastIconElement) {
        setLineStyles({
          height: 0,
          width: 0,
          left: 0,
          top: 0,
          progressHeight: 0,
          progressWidth: 0,
        });
        return;
      }
      const containerRect = containerRef.current.getBoundingClientRect();
      const firstRect = firstIconElement.getBoundingClientRect();
      const lastRect = lastIconElement.getBoundingClientRect();
      if (
        firstRect.width === 0 ||
        firstRect.height === 0 ||
        lastRect.width === 0 ||
        lastRect.height === 0
      ) {
        return;
      }

      if (isMobile) {
        // Horizontal line calculations for mobile
        const firstCenterX =
          firstRect.left + firstRect.width / 2 - containerRect.left;
        const lastCenterX =
          lastRect.left + lastRect.width / 2 - containerRect.left;
        const centerY =
          firstRect.top + firstRect.height / 2 - containerRect.top;

        const totalWidth = lastCenterX - firstCenterX;
        let progressWidth = 0;

        if (
          currentFilteredIndex >= 0 &&
          currentFilteredIndex < filteredSteps.length
        ) {
          const currentIconElement = stepRefs.current[currentFilteredIndex];
          if (currentIconElement) {
            const currentRect = currentIconElement.getBoundingClientRect();
            if (currentRect.width > 0 && currentRect.height > 0) {
              const currentCenterX =
                currentRect.left + currentRect.width / 2 - containerRect.left;
              progressWidth = currentCenterX - firstCenterX;
            }
          }
        }

        if (
          currentFilteredIndex === filteredSteps.length - 1 &&
          filteredSteps.length > 1
        ) {
          progressWidth = totalWidth;
        }

        setLineStyles({
          height: 2,
          width: Math.max(0, totalWidth),
          left: firstCenterX,
          top: centerY - 1,
          progressHeight: 2,
          progressWidth: Math.max(0, progressWidth),
        });
      } else {
        // Vertical line calculations for desktop (original logic)
        const left =
          firstRect.left + firstRect.width / 2 - containerRect.left - 1;
        const firstIconCenterY =
          firstRect.top + firstRect.height / 2 - containerRect.top;
        const lastIconCenterY =
          lastRect.top + lastRect.height / 2 - containerRect.top;
        const totalHeight = lastIconCenterY - firstIconCenterY;
        let progressHeight = 0;

        if (
          currentFilteredIndex >= 0 &&
          currentFilteredIndex < filteredSteps.length
        ) {
          const currentIconElement = stepRefs.current[currentFilteredIndex];
          if (currentIconElement) {
            const currentIconRect = currentIconElement.getBoundingClientRect();
            if (currentIconRect.width > 0 && currentIconRect.height > 0) {
              const currentIconCenterY =
                currentIconRect.top +
                currentIconRect.height / 2 -
                containerRect.top;
              progressHeight = currentIconCenterY - firstIconCenterY;
            }
          }
        }

        if (
          currentFilteredIndex === filteredSteps.length - 1 &&
          filteredSteps.length > 1
        ) {
          progressHeight = totalHeight;
        }

        setLineStyles({
          height: Math.max(0, totalHeight),
          width: 2,
          left,
          top: firstIconCenterY,
          progressHeight: Math.max(0, progressHeight),
          progressWidth: 2,
        });
      }
    };

    calculateLinePositions();
    let resizeTimeout;
    const debouncedCalculate = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateLinePositions, 150);
    };
    window.addEventListener("resize", debouncedCalculate);
    const observer = new MutationObserver(calculateLinePositions);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
    return () => {
      window.removeEventListener("resize", debouncedCalculate);
      clearTimeout(resizeTimeout);
      observer.disconnect();
    };
    // Depend on the calculated filtered index and steps
  }, [currentFilteredIndex, filteredSteps, isMobile]);

  useEffect(() => {
    // Adjust refs array based on the filtered list length
    stepRefs.current = stepRefs.current.slice(0, filteredSteps.length);
  }, [filteredSteps]);

  const handleStepClick = (index) => {
    // 'index' is the filtered index, get the original to pass to the parent
    const originalIndex = filteredSteps[index].originalIndex;
    if (
      (originalIndex <= currentStepIndex ||
        stepDataStatus[originalIndex] === true) &&
      onStepClick
    ) {
      onStepClick(originalIndex);
    }
  };

  const lineTopPosition =
    stepRefs.current[0] && stepRefs.current[0].offsetParent
      ? stepRefs.current[0].offsetTop + stepRefs.current[0].offsetHeight / 2
      : 0;

  return (
    <div className={`sidebar-wrapper ${isMobile ? "mobile" : "desktop"}`}>
      <div
        className={`relative flex flex-col items-center w-full bg-white px-6 py-8 pb-24 ${
          isMobile ? "" : "min-h-screen"
        }`}
      >
        <div className="w-full flex-1">
          <div
            className={`${
              isMobile
                ? "mb-6 text-center logo-container mobile"
                : "mb-10 text-center"
            }`}
          >
            <img
              src={Logo}
              alt="Skill Match AI Logo"
              className={isMobile ? "h-8" : "h-10"}
            />
          </div>
          <div
            ref={containerRef}
            className={`relative flex ${
              isMobile
                ? "flex-row justify-between items-center px-4 overflow-x-auto"
                : "flex-col items-center"
            } w-full steps-container`}
          >
            {filteredSteps.length > 1 &&
              (lineStyles.height > 0 || lineStyles.width > 0) && (
                <div
                  className="absolute bg-gray-200 transition-all duration-500 progress-line-bg"
                  style={
                    isMobile
                      ? {
                          left: `${lineStyles.left}px`,
                          top: `${lineStyles.top}px`,
                          width: `${lineStyles.width}px`,
                          height: `${lineStyles.height}px`,
                          zIndex: 0,
                        }
                      : {
                          left: `${lineStyles.left}px`,
                          top: `${lineTopPosition}px`,
                          height: `${lineStyles.height}px`,
                          width: `${lineStyles.width}px`,
                          zIndex: 0,
                        }
                  }
                />
              )}

            {currentFilteredIndex >= 0 &&
              filteredSteps.length > 1 &&
              (lineStyles.progressHeight > 0 ||
                lineStyles.progressWidth > 0) && (
                <div
                  className="absolute transition-all duration-500 progress-line-active"
                  style={
                    isMobile
                      ? {
                          backgroundColor: "#063326",
                          left: `${lineStyles.left}px`,
                          top: `${lineStyles.top}px`,
                          width: `${lineStyles.progressWidth}px`,
                          height: `${lineStyles.progressHeight}px`,
                          zIndex: 0,
                        }
                      : {
                          backgroundColor: "#063326",
                          left: `${lineStyles.left}px`,
                          top: `${lineTopPosition}px`,
                          height: `${lineStyles.progressHeight}px`,
                          width: `${lineStyles.progressWidth}px`,
                          zIndex: 0,
                        }
                  }
                />
              )}

            {/* Map over the filtered list of steps */}
            {filteredSteps.map((step, index) => {
              const originalIndex = step.originalIndex;
              const isClickable =
                originalIndex <= currentStepIndex ||
                stepDataStatus[originalIndex] === true;

              if (isMobile) {
                return (
                  <div
                    key={step.id || originalIndex}
                    className="step-item-mobile"
                  >
                    <div
                      className={`flex flex-col items-center ${
                        isClickable ? "cursor-pointer" : "cursor-not-allowed"
                      }`}
                      onClick={() => handleStepClick(index)}
                    >
                      <div
                        ref={(el) => (stepRefs.current[index] = el)}
                        className="w-10 h-10 rounded-md z-10 flex items-center justify-center bg-white step-icon-wrapper"
                      >
                        <img
                          src={getIconForStep(step, index)}
                          alt={step.label || step.title || `Step ${index + 1}`}
                          className="w-full h-full"
                        />
                      </div>
                      <div className="mt-2 text-center">
                        <h3
                          className={`text-xs font-medium ${
                            index === currentFilteredIndex
                              ? "text-[#424242]"
                              : originalIndex < currentStepIndex ||
                                stepDataStatus[originalIndex] === true
                              ? "text-[#959595]"
                              : "text-gray-500"
                          }`}
                        >
                          {step.label || step.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // Desktop layout - original code
                return (
                  <div
                    key={step.id || originalIndex}
                    className="w-full mb-10 flex justify-center"
                  >
                    <div
                      className={`flex items-start max-w-[280px] w-full ${
                        isClickable ? "cursor-pointer" : "cursor-not-allowed"
                      }`}
                      onClick={() => handleStepClick(index)}
                    >
                      <div
                        ref={(el) => (stepRefs.current[index] = el)}
                        className="w-10 h-10 rounded-md z-10 flex items-center justify-center bg-white"
                      >
                        <img
                          src={getIconForStep(step, index)}
                          alt={step.label || step.title || `Step ${index + 1}`}
                          className="w-full h-full"
                        />
                      </div>
                      <div className="ml-4 flex-1">
                        <h3
                          className={`text-sm font-medium pt-[0.6rem] ${
                            index === currentFilteredIndex
                              ? "text-[#424242]"
                              : originalIndex < currentStepIndex ||
                                stepDataStatus[originalIndex] === true
                              ? "text-[#959595]"
                              : "text-gray-500"
                          }`}
                        >
                          {step.label || step.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </div>

        {/* Bottom action buttons */}
        <div
          className={`${
            isMobile ? "fixed" : "absolute"
          } bottom-0 left-0 right-0 w-full bg-white px-6 ${
            isMobile ? "pb-4 pt-3" : "pb-8 pt-6"
          } border-t border-gray-200`}
          style={isMobile ? { zIndex: 999999 } : {}}
        >
          <div
            className={`flex ${
              isMobile ? "flex-row gap-2" : "flex-col items-center space-y-3"
            }`}
          >
            {/* Clear Data Button */}
            {onClearData && (
              <button
                className={`flex items-center justify-center ${
                  isMobile ? "flex-1" : "w-full max-w-[280px]"
                } px-4 py-2.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed`}
                onClick={handleClearClick}
                disabled={isClearing}
              >
                <span className={`${isMobile ? "mr-1" : "mr-2"}`}>
                  <TrashIcon />
                </span>
                <span className={`${isMobile ? "hidden sm:inline" : ""}`}>
                  Clear All Data
                </span>
              </button>
            )}

            {/* Logout Button */}
            {onLogout && (
              <button
                className={`flex items-center justify-center ${
                  isMobile ? "flex-1" : "w-full max-w-[280px]"
                } px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
                onClick={onLogout}
              >
                <span className={`${isMobile ? "mr-1" : "mr-2"}`}>
                  <LogoutIcon />
                </span>
                <span className={`${isMobile ? "hidden sm:inline" : ""}`}>
                  Log Out
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Clear Data Confirmation Modal */}
        <ClearDataModal
          isOpen={showClearModal}
          onClose={handleClearCancel}
          onConfirm={handleClearConfirm}
          isClearing={isClearing}
        />
      </div>
    </div>
  );
};

export default Sidebar;
